import React from 'react';
import PropTypes from 'prop-types';
import Tooltip from '../../Tooltip/Tooltip';
import useIsTextOverflow from '../../../customHooks/useIsTextOverflow';
import MaybeWithTooltip from '../../../helpers/MaybeWithTooltip';
import {
  HelpIcon,
  ItemLabelName,
  ItemLabelValue,
  LabelContainer,
  EndAdornmentContainer,
  StartAdornmentContainer,
  ValueContainer
} from './ChipLabel.style';
import {
  generateTooltipValue,
  GET_LABEL_RENDER_BY_CONTROL_TYPE
} from './utils';
import { chipSelectionClasses as classes } from '../chipSelectionClasses';

const ChipLabel = ({
  label,
  startAdornment,
  endAdornment,
  chipDefaultValue,
  controlType,
  renderLabel,
  value,
  helpTooltipContent,
  HelpTooltipProps,
  LabelTooltipProps,
  disabled,
  maxSelectedItemsToDisplay,
  CounterBadgeProps
}) => {
  const shouldRenderCounterBadge =
    controlType === 'multiSelect' && value.length > 1;
  const [isLabelValueOverflow, itemLabelValueElementRef] = useIsTextOverflow(
    value
  );

  if (renderLabel) {
    return renderLabel({ chipDefaultValue, value });
  }

  if (!controlType) {
    return null;
  }

  const itemLabelValue = GET_LABEL_RENDER_BY_CONTROL_TYPE[controlType]({
    chipDefaultValue,
    value,
    disabled,
    maxSelectedItemsToDisplay,
    LabelTooltipProps,
    CounterBadgeProps
  });

  const tooltip = generateTooltipValue({
    controlType,
    shouldRenderCounterBadge,
    value,
    itemLabelValue
  });

  const shouldShowLabelTooltip =
    tooltip && (isLabelValueOverflow || shouldRenderCounterBadge);

  return (
    <LabelContainer
      className={classes.labelContainer}
      data-qa-id="chip-selection-label-container">
      {helpTooltipContent && (
        <Tooltip
          className={classes.helpTooltip}
          placement="top"
          variant="info"
          title={helpTooltipContent}
          {...HelpTooltipProps}>
          <HelpIcon color="action" fontSize="small" />
        </Tooltip>
      )}
      {startAdornment && (
        <StartAdornmentContainer className={classes.startAdornment}>
          {startAdornment}
        </StartAdornmentContainer>
      )}
      {label && (
        <ItemLabelName
          className={classes.labelName}
          variant="body2"
          component="span"
          data-qa-id="chip-selection-label"
          disabled={disabled}>
          {label}
        </ItemLabelName>
      )}

      <MaybeWithTooltip
        className={classes.labelTooltip}
        withTooltip={!!tooltip}
        title={tooltip}
        data-qa-values={tooltip}
        placement="bottom"
        disableHoverListener={!shouldShowLabelTooltip}
        {...LabelTooltipProps}>
        <ValueContainer className={classes.labelValueContainer}>
          <ItemLabelValue
            className={classes.labelValue}
            variant="body2"
            component="span"
            data-qa-id="chip-selection-value"
            ref={itemLabelValueElementRef}
            disabled={disabled}>
            {itemLabelValue}
          </ItemLabelValue>
        </ValueContainer>
      </MaybeWithTooltip>

      {endAdornment && (
        <EndAdornmentContainer className={classes.endAdornment}>
          {endAdornment}
        </EndAdornmentContainer>
      )}
    </LabelContainer>
  );
};

ChipLabel.propTypes = {
  label: PropTypes.node,
  startAdornment: PropTypes.node,
  endAdornment: PropTypes.node,
  chipDefaultValue: PropTypes.string,
  controlType: PropTypes.oneOf([
    'multiSelect',
    'select',
    'dateRange',
    'datePicker',
    'appSelect',
    'multiAppSelect',
    'bundle'
  ]),
  renderLabel: PropTypes.func,
  renderComponent: PropTypes.func,
  value: PropTypes.any,
  disabled: PropTypes.bool,
  helpTooltipContent: PropTypes.node,
  /**
   * props available in [Tooltip](./index.html?path=/docs/components-feedback-tooltip--docs) component
   **/
  HelpTooltipProps: PropTypes.object,
  LabelTooltipProps: PropTypes.object,
  /**
   * props available in [Badge](./index.html?path=/docs/components-data-display-badge--docs) component
   **/
  CounterBadgeProps: PropTypes.object
};

export default ChipLabel;
